import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { ReactElement, useState, useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Container, Skeleton, Box, Typography } from '@mui/material'
import { useForm } from '@tanstack/react-form'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchAssignmentDetailV4,
  fetchLinkedServices,
  fetchCourses,
  fetchTrainingSeries,
  fetchAssignmentSummariesV4,
  selectLinkedServicesStatus,
  selectCourses,
  selectCoursesStatus,
  selectTrainingSeries,
  selectTrainingSeriesStatus,
  fetchLearningPaths,
  fetchFoamfratLearningPaths,
  selectLearningPathsByLinkedService,
  selectLearningPathsStatus,
  selectFoamfratLearningPathsStatus,
  selectActiveAssignmentDetailV4,
  selectActiveAssignmentDetailV4Status,
  fetchCompletedAssignmentsWithinThreshold,
  selectCompletedAssignmentsWithinThreshold,
  selectCompletedAssignmentsWithinThresholdStatus,
  fetchLightweightUsersV4,
  selectLightweightUsersV4ByService,
  selectLightweightUsersV4StatusByService,
  fetchGroups,
  selectGroupsWithDefaults,
  selectGroupsStatus,
  selectUserDetailsV4,
  selectUserDetailsV4Status,
  fetchUserDetailsV4,
} from 'src/v3/manage/manage.slice'
import { Cancel } from '@mui/icons-material'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import WizardNavigation from './components/form-wizard-navigation'
import AssignmentDetailsCoursesStep from './components/form-details-courses-step'
import AssignmentUserSelectionStep from './components/form-user-selection-step'
import AssignmentReviewStep from './components/form-review-step'
import ConfirmationModal from '../components/modals/confirmation-modal'
import SummaryModal from './components/modals/summary-modal'
import ThresholdModal from './components/modals/threshold-modal'
import WarningModal from './components/modals/warning-modal'
import PageTitleWithLinkedService from '../components/page-title-with-linked-service'
import { confirmThreshholdCompletions, updateAssignment } from '../utils/api'
import { EditAssignmentFormData, WizardStep } from './types'
import { useNotifications } from '../hooks/use-notifications'
import { useLinkedServiceName } from '../hooks/use-linked-service-name'
import { ASSIGNMENTS_URLS } from '../constants'

dayjs.extend(utc)

export default function AssignmentEditV4(): ReactElement {
  let navigate = useNavigate()
  let dispatch = useAppDispatch()
  let { assignmentID } = useParams()

  let [step, setStep] = useState<WizardStep>('details-courses')
  let [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  let [isSubmitting, setIsSubmitting] = useState(false)
  let { showError, NotificationComponent } = useNotifications()
  let [summaryModalOpen, setSummaryModalOpen] = useState(false)
  let [summaryModalType, setSummaryModalType] = useState<'users' | 'courses'>(
    'users',
  )
  let [thresholdDialogOpen, setThresholdDialogOpen] = useState(false)
  let [isThresholdSubmitting, setIsThresholdSubmitting] = useState(false)
  let [shouldCheckThreshold, setShouldCheckThreshold] = useState(false)
  let [warningModalOpen, setWarningModalOpen] = useState(false)
  let [usersWithProgress, setUsersWithProgress] = useState<string[]>([])
  let [pendingFormData, setPendingFormData] = useState<any>(null)
  let [originalUserIDs, setOriginalUserIDs] = useState<string[]>([])
  let [originalCourseIDs, setOriginalCourseIDs] = useState<string[]>([])
  let [isFormInitialized, setIsFormInitialized] = useState(false)

  let assignment = useAppSelector(selectActiveAssignmentDetailV4)
  let assignmentDetailStatus = useAppSelector(
    selectActiveAssignmentDetailV4Status,
  )
  let linkedServicesStatus = useAppSelector(selectLinkedServicesStatus)
  let courses = useAppSelector(selectCourses)
  let coursesStatus = useAppSelector(selectCoursesStatus)
  let trainingSeries = useAppSelector(selectTrainingSeries)
  let trainingSeriesStatus = useAppSelector(selectTrainingSeriesStatus)
  let learningPaths = useAppSelector(
    selectLearningPathsByLinkedService(assignment?.linkedServiceID || ''),
  )
  let learningPathsStatus = useAppSelector(selectLearningPathsStatus)
  let foamfratLearningPathsStatus = useAppSelector(
    selectFoamfratLearningPathsStatus,
  )
  let userDetails = useAppSelector(selectUserDetailsV4)
  let userDetailsStatus = useAppSelector(selectUserDetailsV4Status)
  let completedAssignmentsWithinThreshold = useAppSelector(
    selectCompletedAssignmentsWithinThreshold(assignmentID!),
  )
  let completedAssignmentsWithinThresholdStatus = useAppSelector(
    selectCompletedAssignmentsWithinThresholdStatus(assignmentID!),
  )
  let lightweightUsers = useAppSelector(
    selectLightweightUsersV4ByService(assignment?.linkedServiceID || ''),
  )
  let lightweightUsersStatus = useAppSelector(
    selectLightweightUsersV4StatusByService(assignment?.linkedServiceID || ''),
  )
  let groups = useAppSelector(
    selectGroupsWithDefaults(assignment?.linkedServiceID || ''),
  )
  let groupsStatus = useAppSelector(selectGroupsStatus)

  let linkedServiceName = useLinkedServiceName(assignment?.linkedServiceID)
  let successUrl = `${ASSIGNMENTS_URLS.INDEX}/${assignmentID}`
  let updateSuccessMessage = `Assignment "${assignment?.name}" updated successfully.`

  // Check if any assigned user has progress on the assignment
  let hasAnyProgress = useMemo(
    () =>
      assignment?.users?.some((user) => user.completedCourses.length > 0) ||
      false,
    [assignment?.users],
  )

  let handleUpdate = async (formData: EditAssignmentFormData) => {
    setIsSubmitting(true)

    let payload = {
      id: assignmentID!,
      linkedServiceID: formData.linkedServiceID,
      name: formData.name,
      dueDate: formData.dueDate,
      userIDs: formData.userIDs,
      courseIDs: formData.courseIDs,
    }

    await updateAssignment(payload)
      .then(async () => {
        await Promise.all([
          dispatch(fetchAssignmentDetailV4(assignmentID!)),
          dispatch(fetchAssignmentSummariesV4()),
        ])

        let newlyAddedUsers = formData.userIDs.filter(
          (id) => !originalUserIDs.includes(id),
        )

        if (newlyAddedUsers.length > 0) {
          setShouldCheckThreshold(true)
          dispatch(fetchCompletedAssignmentsWithinThreshold(assignmentID!))
        } else {
          navigate(successUrl, {
            state: {
              successMessage: updateSuccessMessage,
            },
          })
        }
      })
      .catch((err: any) => {
        let errorMessage = `Error updating assignment ${assignment?.name}.`
        // eslint-disable-next-line no-console
        console.error(errorMessage, 'Payload:', payload, 'Error:', err)
        showError(errorMessage)
      })
      .finally(() => {
        setIsSubmitting(false)
        setPendingFormData(null)
        setWarningModalOpen(false)
      })
  }

  let form = useForm({
    defaultValues: {
      name: '',
      linkedServiceID: '',
      dueDate: '',
      userIDs: [] as string[],
      courseIDs: [] as string[],
    } as EditAssignmentFormData,
    validators: {
      onChange: ({ value }) => {
        let fieldErrors: Array<[string, string]> = [
          ...(!value.name
            ? [['name', 'Assignment name is required'] as [string, string]]
            : []),
          ...(!value.linkedServiceID
            ? [
                ['linkedServiceID', 'Linked service is required'] as [
                  string,
                  string,
                ],
              ]
            : []),
          ...(!value.dueDate
            ? [['dueDate', 'Due date is required'] as [string, string]]
            : []),
          ...(!value.courseIDs || value.courseIDs.length === 0
            ? [
                ['courseIDs', 'At least one course must be selected'] as [
                  string,
                  string,
                ],
              ]
            : []),
          ...(!value.userIDs || value.userIDs.length === 0
            ? [
                ['userIDs', 'At least one user must be selected'] as [
                  string,
                  string,
                ],
              ]
            : []),
        ]

        return fieldErrors.length > 0
          ? { fields: Object.fromEntries(fieldErrors) }
          : undefined
      },
    },
    onSubmit: async ({ value }) => {
      let currentOriginalUserIDs = assignment?.users?.map((u) => u.id) || []
      let removedUsers = currentOriginalUserIDs.filter(
        (id) => !value.userIDs.includes(id),
      )

      if (removedUsers.length > 0) {
        // Check if any removed users have progress
        let usersWithProgressToRemove = removedUsers.filter((userId) => {
          let user = assignment?.users?.find((u) => u.id === userId)
          return user && user.completedCourses.length > 0
        })

        if (usersWithProgressToRemove.length > 0) {
          setUsersWithProgress(usersWithProgressToRemove)
          setPendingFormData(value)
          setWarningModalOpen(true)
          return
        }
      }

      await handleUpdate(value)
    },
  })

  let handleThresholdConfirm = async () => {
    setIsThresholdSubmitting(true)

    await confirmThreshholdCompletions(assignmentID!)
      .then(async () => {
        // Refresh assignment data in Redux before navigating
        await Promise.all([
          dispatch(fetchAssignmentDetailV4(assignmentID!)),
          dispatch(fetchAssignmentSummariesV4()),
        ])

        navigate(successUrl, {
          state: {
            successMessage: `${updateSuccessMessage} Users within threshold have been marked complete.`,
          },
        })
      })
      .catch((err: any) => {
        let errorMessage = `Failed to process threshold user completions for Assignment ${assignment?.name}.`
        // eslint-disable-next-line no-console
        console.error(
          errorMessage,
          'Assignment ID: ',
          assignmentID,
          'Error: ',
          err,
        )
        showError(errorMessage)
      })
      .finally(() => {
        setIsThresholdSubmitting(false)
        setThresholdDialogOpen(false)
      })
  }

  let handleThresholdDecline = () => {
    setThresholdDialogOpen(false)
    navigate(successUrl, {
      state: {
        successMessage: updateSuccessMessage,
      },
    })
  }

  let handleConfirmRemoval = () => {
    if (pendingFormData) {
      handleUpdate(pendingFormData)
    }
  }

  let handleStepChange = (newStep: WizardStep) => {
    setStep(newStep)
  }

  let handleCancel = (formValues?: any) => {
    if (formValues && isFormInitialized) {
      let hasChanges =
        formValues.name !== assignment?.name ||
        formValues.dueDate !== assignment?.dueDate ||
        formValues.userIDs?.length !== originalUserIDs.length ||
        formValues.courseIDs?.length !== originalCourseIDs.length ||
        formValues.userIDs?.some(
          (id: string) => !originalUserIDs.includes(id),
        ) ||
        formValues.courseIDs?.some(
          (id: string) => !originalCourseIDs.includes(id),
        )

      if (hasChanges) {
        setCancelDialogOpen(true)
      } else {
        navigate(successUrl)
      }
    } else {
      navigate(successUrl)
    }
  }

  // Helper to calculate changes summary
  let getChangesSummary = (
    currentUserIDs: string[],
    currentCourseIDs: string[],
  ) => {
    let addedUsers = currentUserIDs.filter(
      (id) => !originalUserIDs.includes(id),
    )
    let removedUsers = originalUserIDs.filter(
      (id) => !currentUserIDs.includes(id),
    )
    let addedCourses = currentCourseIDs.filter(
      (id) => !originalCourseIDs.includes(id),
    )
    let removedCourses = originalCourseIDs.filter(
      (id) => !currentCourseIDs.includes(id),
    )

    return {
      addedUsers,
      removedUsers,
      addedCourses,
      removedCourses,
      hasChanges:
        addedUsers.length > 0 ||
        removedUsers.length > 0 ||
        addedCourses.length > 0 ||
        removedCourses.length > 0,
    }
  }

  // Fetch user details for currently assigned users
  let assignedUserIDs = useMemo(
    () => assignment?.users?.map((u) => u.id) || [],
    [assignment?.users],
  )
  let missingUserIDs = useMemo(
    () => (assignedUserIDs ?? []).filter((id) => !userDetails[id]) ?? [],
    [assignedUserIDs, userDetails],
  )

  useAutoFetch(linkedServicesStatus, fetchLinkedServices)
  useAutoFetch(coursesStatus, fetchCourses)
  useAutoFetch(trainingSeriesStatus, fetchTrainingSeries)
  useAutoFetch(foamfratLearningPathsStatus, fetchFoamfratLearningPaths)

  useEffect(() => {
    if (
      assignmentDetailStatus === 'uninitialized' ||
      assignment?.id !== assignmentID
    ) {
      dispatch(fetchAssignmentDetailV4(assignmentID!))
    }
  }, [assignmentID, assignmentDetailStatus, assignment?.id, dispatch])

  // Reset form initialization flag when assignment ID changes
  useEffect(() => {
    setIsFormInitialized(false)
  }, [assignmentID])

  useEffect(() => {
    if (
      learningPathsStatus === 'uninitialized' &&
      assignment?.linkedServiceID &&
      assignment.linkedServiceID !== 'unselected'
    ) {
      dispatch(fetchLearningPaths(assignment.linkedServiceID))
    }
  }, [learningPathsStatus, assignment?.linkedServiceID, dispatch])

  // Get specific status for missing users to avoid infinite loops
  let missingUserStatuses = useMemo(
    () => missingUserIDs.map((id) => userDetailsStatus[id] || 'uninitialized'),
    [missingUserIDs, userDetailsStatus],
  )

  useEffect(() => {
    if (missingUserIDs.length > 0) {
      let usersToFetch = missingUserIDs.filter((userId) => {
        let status = userDetailsStatus[userId]
        return status !== 'fulfilled' && status !== 'pending'
      })

      if (usersToFetch.length > 0) {
        dispatch(fetchUserDetailsV4(usersToFetch))
      }
    }
  }, [missingUserIDs, missingUserStatuses, dispatch])

  useEffect(() => {
    if (
      assignment?.linkedServiceID &&
      lightweightUsersStatus !== 'fulfilled' &&
      lightweightUsersStatus !== 'pending'
    ) {
      dispatch(fetchLightweightUsersV4(assignment?.linkedServiceID || ''))
    }
  }, [assignment?.linkedServiceID, lightweightUsersStatus, dispatch])

  useEffect(() => {
    if (groupsStatus !== 'fulfilled' && groupsStatus !== 'pending') {
      dispatch(fetchGroups())
    }
  }, [groupsStatus, dispatch])

  // Update form when assignment data loads
  useEffect(() => {
    if (assignment && assignment.id === assignmentID && !isFormInitialized) {
      let userIDs = assignment.users?.map((u) => u.id) || []
      let courseIDs = assignment.courses?.map((c) => c.id) || []

      form.setFieldValue('name', assignment.name)
      form.setFieldValue('linkedServiceID', assignment.linkedServiceID)
      form.setFieldValue(
        'dueDate',
        assignment.dueDate
          ? dayjs(assignment.dueDate).format('YYYY-MM-DD')
          : '',
      )
      form.setFieldValue('userIDs', userIDs)
      form.setFieldValue('courseIDs', courseIDs)

      // Store original values for diff
      setOriginalUserIDs(userIDs)
      setOriginalCourseIDs(courseIDs)
      setIsFormInitialized(true)
    }
  }, [assignment, isFormInitialized, assignmentID])

  // Threshold detection effect
  useEffect(() => {
    if (
      shouldCheckThreshold &&
      completedAssignmentsWithinThresholdStatus === 'fulfilled'
    ) {
      // Filter threshold results to only include newly added users
      let currentFormData = form.getFieldValue('userIDs') || []
      let newlyAddedUsers = currentFormData.filter(
        (id: string) => !originalUserIDs.includes(id),
      )

      let thresholdForNewUsers =
        completedAssignmentsWithinThreshold?.filter((threshold) =>
          newlyAddedUsers.includes(threshold.userID),
        ) || []

      if (thresholdForNewUsers.length > 0) {
        setThresholdDialogOpen(true)
      } else {
        navigate(successUrl, {
          state: {
            successMessage: updateSuccessMessage,
          },
        })
      }
      setShouldCheckThreshold(false)
    }
  }, [
    shouldCheckThreshold,
    completedAssignmentsWithinThresholdStatus,
    completedAssignmentsWithinThreshold,
    originalUserIDs,
    form,
    assignmentID,
    navigate,
  ])

  if (
    assignmentDetailStatus === 'pending' ||
    !assignment ||
    !isFormInitialized
  ) {
    return (
      <Container maxWidth="xl" className="py-6">
        <Box sx={{ mb: 3 }}>
          <Skeleton variant="text" width={300} height={40} animation="wave" />
          <Skeleton variant="text" width={200} height={24} animation="wave" />
        </Box>
        <Skeleton
          variant="rectangular"
          height={60}
          animation="wave"
          sx={{ mb: 3 }}
        />
        <Skeleton variant="rectangular" height={400} animation="wave" />
      </Container>
    )
  }

  return (
    <Container maxWidth="xl" className="py-6">
      <PageTitleWithLinkedService
        pageTitle={`Edit Assignment: ${assignment.name}`}
        moduleName="Assignments"
        linkedServiceName={linkedServiceName || ''}
        handleBackClick={handleCancel}
      />
      <WizardNavigation currentStep={step} onStepChange={handleStepChange} />
      {step === 'details-courses' && (
        <form.Subscribe
          selector={(state) => ({
            values: state.values,
            fieldMeta: state.fieldMeta,
          })}
        >
          {({ values, fieldMeta }) => {
            let canContinue = Boolean(
              values.name &&
              fieldMeta.name?.isValid &&
              !fieldMeta.name?.errors?.length &&
              values.linkedServiceID &&
              fieldMeta.linkedServiceID?.isValid &&
              !fieldMeta.linkedServiceID?.errors?.length &&
              values.dueDate &&
              fieldMeta.dueDate?.isValid &&
              !fieldMeta.dueDate?.errors?.length &&
              values.courseIDs?.length > 0 &&
              fieldMeta.courseIDs?.isValid &&
              !fieldMeta.courseIDs?.errors?.length,
            )

            return (
              <AssignmentDetailsCoursesStep
                values={values}
                errors={{}}
                courses={courses}
                trainingSeries={trainingSeries}
                learningPaths={learningPaths}
                form={form}
                onCancel={() => handleCancel(values)}
                onContinue={() => handleStepChange('users')}
                canContinue={canContinue}
                isLoadingCourses={coursesStatus === 'pending'}
                hasAnyProgress={hasAnyProgress}
                linkedServiceID={assignment?.linkedServiceID}
                isEditMode={true}
              />
            )
          }}
        </form.Subscribe>
      )}
      {step === 'users' && (
        <form.Field name="userIDs" mode="array">
          {(field) => (
            <form.Subscribe
              selector={(state) => ({
                values: state.values,
                fieldMeta: state.fieldMeta,
              })}
            >
              {({ values, fieldMeta }) => {
                let canContinue = Boolean(
                  values.userIDs?.length > 0 &&
                  fieldMeta.userIDs?.isValid &&
                  !fieldMeta.userIDs?.errors?.length,
                )

                return (
                  <AssignmentUserSelectionStep
                    values={{ userIDs: values.userIDs }}
                    errors={{
                      userIDs: field.state.meta.errors?.[0],
                    }}
                    users={lightweightUsers}
                    groups={groups as any}
                    onValueChange={(_, value) => field.handleChange(value)}
                    onCancel={() => handleCancel(values)}
                    onBack={() => setStep('details-courses')}
                    onContinue={() => handleStepChange('review')}
                    canContinue={canContinue}
                    isLoadingUsers={false}
                  />
                )
              }}
            </form.Subscribe>
          )}
        </form.Field>
      )}
      {step === 'review' && (
        <form.Subscribe selector={(state) => state.values}>
          {(formValues) => {
            let changesSummary = getChangesSummary(
              formValues.userIDs,
              formValues.courseIDs,
            )

            return (
              <AssignmentReviewStep
                formData={formValues}
                onCancel={() => handleCancel(formValues)}
                onBack={() => setStep('users')}
                onSubmit={() => form.handleSubmit()}
                onShowUsersSummary={() => {
                  setSummaryModalType('users')
                  setSummaryModalOpen(true)
                }}
                onShowCoursesSummary={() => {
                  setSummaryModalType('courses')
                  setSummaryModalOpen(true)
                }}
                isSubmitting={isSubmitting}
                isEditMode={true}
                changesSummary={changesSummary}
                users={lightweightUsers}
                courses={courses}
              />
            )
          }}
        </form.Subscribe>
      )}
      <ConfirmationModal
        isOpen={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        onConfirm={() => navigate(`${ASSIGNMENTS_URLS.INDEX}/${assignmentID}`)}
        title="Cancel Changes?"
        confirmText="Yes, Cancel"
        cancelText="Continue Editing"
        confirmColor="error"
        confirmStartIcon={<Cancel />}
      >
        <Typography>
          Are you sure you want to cancel? Any changes will be lost.
        </Typography>
      </ConfirmationModal>
      <WarningModal
        isOpen={warningModalOpen}
        onClose={() => setWarningModalOpen(false)}
        onConfirm={handleConfirmRemoval}
        title="Remove Users with Progress?"
        message={`You are about to remove ${usersWithProgress.length} user(s) who have already started this assignment. Their progress will be lost.`}
        confirmText="Yes, Remove Users"
        cancelText="Cancel"
      />
      <form.Subscribe
        selector={(state) => [state.values.userIDs, state.values.courseIDs]}
      >
        {([userIDs, courseIDs]) => (
          <SummaryModal
            isOpen={summaryModalOpen}
            onClose={() => setSummaryModalOpen(false)}
            type={summaryModalType}
            users={lightweightUsers}
            courses={courses}
            selectedUserIDs={userIDs}
            selectedCourseIDs={courseIDs}
          />
        )}
      </form.Subscribe>
      <ThresholdModal
        open={thresholdDialogOpen}
        onClose={handleThresholdDecline}
        onConfirm={handleThresholdConfirm}
        completedAssignmentsWithinThreshold={
          // Filter to only show newly added users
          completedAssignmentsWithinThreshold?.filter((threshold) => {
            let currentFormData = form.getFieldValue('userIDs') || []
            let newlyAddedUsers = currentFormData.filter(
              (id: string) => !originalUserIDs.includes(id),
            )
            return newlyAddedUsers.includes(threshold.userID)
          }) || []
        }
        users={lightweightUsers}
        courses={courses}
        isSubmitting={isThresholdSubmitting}
      />
      {NotificationComponent}
    </Container>
  )
}
